import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { BackendGameListItem, BackendGameConfigResponse } from '@core/types/backend-game.types';
import { fetchGamesList, fetchGameConfig } from '../services/backend-game/BackendGameApiService';

interface BackendGameState {
  games: BackendGameListItem[];
  gamesLoading: boolean;
  gamesError: string | null;
  configCache: Record<string, BackendGameConfigResponse>;
  configLoading: Record<string, boolean>;
}

interface BackendGameActions {
  loadGamesList: () => Promise<void>;
  loadGameConfig: (gameName: string) => Promise<BackendGameConfigResponse>;
  clearCache: () => void;
}

export const useBackendGameStore = create<BackendGameState & BackendGameActions>()(
  devtools(
    (set, get) => ({
      games: [],
      gamesLoading: false,
      gamesError: null,
      configCache: {},
      configLoading: {},

      loadGamesList: async () => {
        set({ gamesLoading: true, gamesError: null });
        try {
          const games = await fetchGamesList();
          set({ games, gamesLoading: false });
        } catch (err) {
          set({ gamesError: (err as Error).message, gamesLoading: false });
        }
      },

      loadGameConfig: async (gameName: string) => {
        const cached = get().configCache[gameName];
        if (cached) return cached;

        set((s) => ({ configLoading: { ...s.configLoading, [gameName]: true } }));
        try {
          const config = await fetchGameConfig(gameName);
          set((s) => ({
            configCache: { ...s.configCache, [gameName]: config },
            configLoading: { ...s.configLoading, [gameName]: false },
          }));
          return config;
        } catch (err) {
          set((s) => ({ configLoading: { ...s.configLoading, [gameName]: false } }));
          throw err;
        }
      },

      clearCache: () => set({ configCache: {}, configLoading: {} }),
    }),
    { name: 'backend-game-store' }
  )
);
